// src/App.jsx
import React, { useState, useCallback, useRef } from "react";
import { Logo, Header, BottomNav, Toast } from "./components/UI";
import { TodayScreen, DiscoverScreen, MapScreen, SocialScreen, ProfileScreen } from "./screens/Screens";
import { EventDetail, TicketScreen, ConfirmScreen, OnboardingScreen, CreateEventScreen, EditProfileScreen } from "./screens/DetailScreens";
import { useStorage } from "./hooks/useStorage";
import { SEED_EVENTS, SEED_COMMENTS } from "./data/events";

// Lucide-style icon components (inline SVG, no extra dep needed)
const IconClock  = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const IconSearch = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>;
const IconMap    = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/></svg>;
const IconUsers  = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"/></svg>;
const IconUser   = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const IconBell   = ({size,color}) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>;

const TABS = [
  { id:"today",    label:"Today",    Icon: IconClock  },
  { id:"discover", label:"Discover", Icon: IconSearch },
  { id:"map",      label:"Map",      Icon: IconMap    },
  { id:"social",   label:"Social",   Icon: IconUsers  },
  { id:"me",       label:"Me",       Icon: IconUser   },
];

export default function App() {
  const [user,     setUser]     = useStorage("user",     null);
  const [events,   setEvents]   = useStorage("events",   SEED_EVENTS);
  const [comments, setComments] = useStorage("comments", SEED_COMMENTS);
  const [saved,    setSaved]    = useStorage("saved",    []);
  const [tab,      setTab]      = useState("today");
  const [stack,    setStack]    = useState([]);
  const [toast,    setToast]    = useState(null);
  const toastTimer = useRef(null);

  const showToast = useCallback((msg) => {
    setToast(msg);
    clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 2500);
  }, []);

  // Navigation
  const push = (screen) => setStack(s => [...s, screen]);
  const pop  = ()       => setStack(s => s.slice(0, -1));

  // Actions
  const handleSave = useCallback((id) => {
    setSaved(prev => {
      const isSaved = prev.includes(id);
      showToast(isSaved ? "Removed from saved" : "Saved! ✓");
      return isSaved ? prev.filter(x => x !== id) : [...prev, id];
    });
  }, [setSaved, showToast]);

  const handleAddComment = useCallback((evId, comment) => {
    setComments(prev => ({ ...prev, [evId]: [...(prev[evId] || []), comment] }));
    showToast("Comment posted!");
  }, [setComments, showToast]);

  const handleLike = useCallback((evId, cId) => {
    setComments(prev => ({
      ...prev,
      [evId]: prev[evId].map(c => c.id === cId ? { ...c, liked: !c.liked, likes: c.liked ? c.likes - 1 : c.likes + 1 } : c),
    }));
  }, [setComments]);

  const handleOnboard = useCallback((u) => {
    setUser(u);
    showToast(`Welcome to iPulse, ${u.name}!`);
  }, [setUser, showToast]);

  const handleCreateEvent = useCallback((ev) => {
    setEvents(prev => [...prev, ev]);
    showToast("Event published! 🎉");
    pop();
  }, [setEvents, showToast]);

  const handleSaveProfile = useCallback((u) => {
    setUser(u);
    showToast("Profile saved!");
    pop();
  }, [setUser, showToast]);

  const city = user?.city || "Cape Town";

  // Screen stack resolution
  const cur = stack[stack.length - 1];

  const renderStack = () => {
    if (!cur) return null;
    if (cur.type === "detail") return (
      <EventDetail ev={cur.ev} saved={saved.includes(cur.ev.id)} onSave={handleSave}
        comments={comments[cur.ev.id] || []} onAddComment={handleAddComment} onLike={handleLike}
        onBack={pop} onTickets={(ev) => push({ type:"tickets", ev })}/>
    );
    if (cur.type === "tickets") return (
      <TicketScreen ev={cur.ev} onBack={pop}
        onConfirm={(ev, tier, qty, total) => push({ type:"confirm", ev, tier, qty, total })}/>
    );
    if (cur.type === "confirm") return (
      <ConfirmScreen ev={cur.ev} tier={cur.tier} qty={cur.qty} total={cur.total}
        onDone={() => { setStack([]); setTab("today"); showToast("Enjoy the event!"); }}/>
    );
    if (cur.type === "create") return (
      <CreateEventScreen onBack={pop} onSubmit={handleCreateEvent} city={city}/>
    );
    if (cur.type === "edit-profile") return (
      <EditProfileScreen user={user} onBack={pop} onSave={handleSaveProfile}/>
    );
    return null;
  };

  // Onboarding gate
  if (!user) return (
    <div style={{ maxWidth:420, margin:"0 auto", minHeight:"100vh", background:"#fff", display:"flex", flexDirection:"column" }}>
      <OnboardingScreen onDone={handleOnboard}/>
      <Toast message={toast}/>
    </div>
  );

  const stackScreen = renderStack();

  return (
    <div style={{ maxWidth:420, margin:"0 auto", minHeight:"100vh", background:"#f5f5f7", display:"flex", flexDirection:"column", position:"relative" }}>
      {stackScreen ? (
        <div style={{ display:"flex", flexDirection:"column", flex:1, background:"#fff" }}>
          {stackScreen}
        </div>
      ) : (
        <>
          <Header
            left={<Logo/>}
            right={
              <>
                <button onClick={() => showToast("Notifications: 4 new")} style={{ width:30, height:30, borderRadius:"50%", background:"#f5f5f5", border:"0.5px solid rgba(0,0,0,0.1)", display:"flex", alignItems:"center", justifyContent:"center", cursor:"pointer", position:"relative" }}>
                  <IconBell size={15} color="#555"/>
                  <div style={{ position:"absolute", top:-1, right:-1, width:13, height:13, borderRadius:"50%", background:"#D4537E", color:"#fff", fontSize:8, display:"flex", alignItems:"center", justifyContent:"center" }}>4</div>
                </button>
                <button onClick={() => showToast("City: " + city)} style={{ display:"flex", alignItems:"center", gap:4, background:"#EEEDFE", color:"#534AB7", fontSize:11, padding:"4px 9px", borderRadius:20, cursor:"pointer", border:"none" }}>
                  📍 {city}
                </button>
              </>
            }
          />
          <div style={{ flex:1, overflowY:"auto", display:"flex", flexDirection:"column" }}>
            {tab === "today"    && <TodayScreen    events={events} saved={saved} onSave={handleSave} onOpen={id => push({ type:"detail", ev:events.find(e=>e.id===id) })} city={city}/>}
            {tab === "discover" && <DiscoverScreen events={events} saved={saved} onSave={handleSave} onOpen={id => push({ type:"detail", ev:events.find(e=>e.id===id) })} city={city}/>}
            {tab === "map"      && <MapScreen      events={events} saved={saved} onSave={handleSave} onOpen={id => push({ type:"detail", ev:events.find(e=>e.id===id) })} city={city}/>}
            {tab === "social"   && <SocialScreen onOpenEvent={id => push({ type:"detail", ev:events.find(e=>e.id===id) })}/>}
            {tab === "me"       && <ProfileScreen user={user} saved={saved} events={events} onEdit={() => push({type:"edit-profile"})} onCreateEvent={() => push({type:"create"})}/>}
          </div>
          <BottomNav tabs={TABS} active={tab} onChange={setTab}/>
        </>
      )}
      <Toast message={toast}/>
    </div>
  );
}
